/**
 * Global route middleware that manages A/B experiment lifecycle.
 * Runs on every navigation. Numbered `03` to execute after auth middleware.
 *
 * **Responsibilities:**
 *
 * 1. **Cleanup**: removes cookies for experiments no longer in the active
 *    `core.experiments` config. Runs on both SSR and client so a stale
 *    cookie is wiped from the response before the client sees it —
 *    otherwise `useExperiment`'s readonly path would return the stale
 *    variant on SSR and `'a'` on the client, causing a hydration mismatch.
 *
 * 2. **Assignment**: for each active experiment:
 *    - Skips if versions A and B are not both defined
 *    - Skips if `needsAuthentication` is set and user is not logged in
 *    - Skips `userId` assignment experiments only when there is neither a
 *      user identity nor a pre-existing cookie (returning visitors fall
 *      through so their prior assignment is preserved and seeded into
 *      useState for SSR readonly reads)
 *    - Skips BUNDLES experiment if promotion config is incomplete
 *    - For `userId` assignment with no prior cookie: deterministically sets
 *      cookie via `Math.floor(userId / groupSize) % 2` before calling
 *      useExperiment
 *    - Calls `useExperiment({ allowWrite: true })` to finalize
 *
 * @see CoreExperiment for experiment configuration shape
 * @see useExperiment for the assignment composable
 */
import { ExperimentCookieName, ExperimentValue, type CoreExperiment, ExperimentName } from '@core/types'

export default defineNuxtRouteMiddleware((to) => {
    const { experiments } = useCoreConfig()
    const { signupBundleExperiments } = usePromotionConfig()
    const { isLoggedIn, data: user } = useUserInfo()

    const cleanupInactiveExperiments = () => {
        const activeExperimentSet = new Set(
            experiments?.map(experiment => experiment.cookieName) ?? [],
        )
        const possibleExperiments = Object.values(ExperimentCookieName)

        possibleExperiments.forEach((cookieName) => {
            // Don't remove active experiments
            if (activeExperimentSet.has(cookieName)) return

            const experimentCookie = useCookie(cookieName)

            // If the cookie exists, clear it
            if (experimentCookie.value) {
                experimentCookie.value = null
            }
        })
    }

    cleanupInactiveExperiments()

    if (!experiments?.length) return

    // Set cookie for each active experiment
    experiments.forEach((experiment: CoreExperiment) => {
        // Prevent setting cookie for empty required experiments
        if (!experiment?.versions?.[ExperimentValue.A] || !experiment?.versions?.[ExperimentValue.B]) return

        // Prevent setting cookie for experiments that needs authentication
        if (experiment?.needsAuthentication && !isLoggedIn.value) return

        // Skip userId-method experiments only when there's neither a user
        // identity nor a prior assignment cookie. Returning visitors who
        // carry a cookie from a previous login must fall through so
        // useExperiment(allowWrite) preserves the cookie value and seeds
        // useState — otherwise SSR readonly defaults to 'a' while the client
        // reads the cookie, producing a hydration mismatch / first-paint
        // flicker
        const effectiveId = user.value?.id
        const existingCookie = useCookie<ExperimentValue>(experiment.cookieName)
        if (
            experiment.assignmentMethod === 'userId'
            && !effectiveId
            && !existingCookie.value
        ) return

        if (
            experiment.name === ExperimentName.BUNDLES
            && (!signupBundleExperiments?.a || !signupBundleExperiments?.b)
        ) return

        // Handle userId-based assignment before calling useExperiment
        if (experiment.assignmentMethod === 'userId' && effectiveId && !existingCookie.value) {
            const groupSize = experiment.groupSize ?? 1
            existingCookie.value = Math.floor(Number(effectiveId) / groupSize) % 2 === 0
                ? ExperimentValue.A
                : ExperimentValue.B
        }

        useExperiment({
            cookieName: experiment.cookieName,
            experimentName: experiment.name,
            allowWrite: true
        })
    })
})
